import { serverSidePagePrepare } from "@lib/hooks"
import { observer } from "mobx-react"
import {
  DocumentTemplate,
  Header,
  Page,
  PureLogo,
  useStore,
} from "@features/common"
import { RouterLink } from "@lib/routing"
import { scrollToTop } from "@lib/scroll"
import { IconHamburger } from "@lib/icons"
import {
  DesktopTableOfContents,
  LegalSectionView,
  TermsLogo,
  TouchTableOfContents,
} from "@features/terms"
import { dateFormater } from "@lib/date"
import { redirect } from "@lib/isomorphic"
import { useEffect, useState } from "react"
import { useRouter } from "next/router"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models, ctx }) => {
    const { effective_date } = ctx.query
    const queryDate = Array.isArray(effective_date)
      ? effective_date[0]
      : effective_date

    await Models.FaqModel.init()
    try {
      await Models.LegalModel.getThreateningBehaviorPageData(queryDate)
    } catch {
      redirect(`/${Models.GlobalModel.lang}/not-found`, ctx)
    }
  },
})

const ThreateningBehaviorPage = observer(function ThreateningBehaviorPage() {
  const {
    GlobalModel: { openMenu },
    LegalModel: {
      threateningBehaviorSections,
      threateningBehaviorTitle,
      threateningBehaviorEffectiveDate,
    },
  } = useStore()
  const { asPath } = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    function scrollToAnchor() {
      const hash = asPath.split("#")[1]
      if (hash) {
        const section = document.querySelector(`[data-faq-id=${hash}]`)
        if (section) {
          section.scrollIntoView()
        }
      }
    }
    if (isMounted) {
      setTimeout(() => scrollToAnchor(), 100)
    }
  }, [isMounted, asPath])

  return (
    <Page
      meta={{
        title: "Threatening Behavior and Content",
      }}
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri="/legal/threatening-behaviour-and-content"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3 tablet-m:mt-[20px] tablet-m:mb-[18px]"
            >
              <div className="shrink-0 flex items-stretch h-4 tablet-m:h-7">
                <PureLogo className="shrink-0 block h-full w-auto tablet:h-full text-white pr-2 tablet-m:pr-4" />
                <TermsLogo />
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      seoH1={threateningBehaviorTitle}
    >
      <div className="container pt-3 pb-15 laptop:pt-8 laptop:pb-20 legal-document">
        {threateningBehaviorSections.length > 1 ? (
          <TouchTableOfContents tableOfContents={threateningBehaviorSections} />
        ) : null}
        <DocumentTemplate
          sidebar={
            threateningBehaviorSections.length > 1 ? (
              <DesktopTableOfContents
                tableOfContents={threateningBehaviorSections}
              />
            ) : null
          }
        >
          <section className="space-y-10" id="scrollContainerElement">
            {threateningBehaviorSections.map((i) => (
              <LegalSectionView data={i} key={i.id} id={i.id} />
            ))}
            <div>
              <div className="pb-3">
                <strong>Updated:</strong>{" "}
                {threateningBehaviorEffectiveDate
                  ? dateFormater(
                      new Date(threateningBehaviorEffectiveDate),
                      "MMMM, yyyy",
                      "en",
                    )
                  : ""}
              </div>
            </div>
          </section>
        </DocumentTemplate>
      </div>
    </Page>
  )
})

export default ThreateningBehaviorPage
